import {
  ParticipantEvent,
  RemoteParticipant,
  RemoteTrack,
  RoomEvent,
  Track,
  TrackPublication,
} from 'livekit-client';
import { action, observable, makeObservable } from 'mobx';

import { ProviderMediaType } from '@gem/call/CallProviders/callProviders.constants';
import {
  ICallProviderOpponent,
  OnProviderUserHandAction,
  OnProviderUserMediaChanged,
  OnProviderUserVolumeChanged,
  ProviderUid,
} from '@gem/call/CallProviders/callProviders.types';

import { LiveKitProvider } from './LiveKitProvider';

export class LiveKitProviderOpponent implements ICallProviderOpponent {
  private liveKitSubscriber: RemoteParticipant;
  private onUserMediaChangedHandler: OnProviderUserMediaChanged | undefined;
  private onUserVolumeLevelChanged: OnProviderUserVolumeChanged | undefined;
  private onUserHandAction: OnProviderUserHandAction | undefined;
  private audio: HTMLAudioElement | null = null;
  private parent: LiveKitProvider;
  private isStartingAudio = false;
  private audioPlaybackFailedHandler: (() => Promise<void>) | null = null;
  private audioPlaybackStartedHandler: (() => void) | null = null;
  private restartAudioTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private resumeAudioTimeoutId: ReturnType<typeof setTimeout> | null = null;
  @observable hasAudio = false;
  @observable hasVideo = false;
  @observable isPlayingVideo = false;
  @observable volumeLevel = 0;
  uid: ProviderUid;

  constructor(params: {
    subscriber: RemoteParticipant;
    subscriberId: ProviderUid;
    onUserMediaChanged?: OnProviderUserMediaChanged;
    onUserVolumeLevelChanged?: OnProviderUserVolumeChanged;
    onUserHandAction?: OnProviderUserHandAction;
    parent: LiveKitProvider;
  }) {
    makeObservable(this);
    this.parent = params.parent;
    this.liveKitSubscriber = params.subscriber;
    this.onUserVolumeLevelChanged = params.onUserVolumeLevelChanged;
    this.onUserMediaChangedHandler = params.onUserMediaChanged;
    this.onUserHandAction = params.onUserHandAction;
    this.uid = params.subscriberId;
    this.hasAudio = params.subscriber.isMicrophoneEnabled;
    this.hasVideo = params.subscriber.isCameraEnabled || params.subscriber.isScreenShareEnabled;
    this.liveKitSubscriber.on(RoomEvent.TrackSubscribed, (event: RemoteTrack) => {
      this.handlePublisherChanged(event, true);
    });
    this.liveKitSubscriber.on(RoomEvent.TrackUnsubscribed, (event: RemoteTrack) => {
      this.handlePublisherChanged(event, false);
    });

    params.subscriber.on(ParticipantEvent.TrackMuted, (event: TrackPublication) => {
      this.handlePublisherChanged(event, false);
    });
    params.subscriber.on(ParticipantEvent.TrackUnmuted, (event: TrackPublication) => {
      this.handlePublisherChanged(event, true);
    });

    params.subscriber.on(ParticipantEvent.AttributesChanged, () => {
      this.handleAttributesChanged();
    });

    params.subscriber.on(
      ParticipantEvent.IsSpeakingChanged,
      this.handleVolumeLevelChanged.bind(this)
    );

    if (this.hasAudio) {
      this.startAudioTrack();
    }
  }

  @action handleAttributesChanged(): void {
    const attributes = this.liveKitSubscriber.attributes;
    const { isHandRaised, handRaisedAt } = attributes;

    if (isHandRaised && handRaisedAt) {
      this.onUserHandAction &&
        this.onUserHandAction(this.uid, isHandRaised === 'true', Number(handRaisedAt));
    }
  }

  @action
  playVideo(element: HTMLDivElement): void {
    const video = this.liveKitSubscriber.getTrackPublication(
      this.liveKitSubscriber.isScreenShareEnabled ? Track.Source.ScreenShare : Track.Source.Camera
    );

    if (!video) {
      return;
    }

    // workaround after track unpublish/publish. we need to disable and enable track to void black screen
    video.setEnabled(false);
    video.setEnabled(true);

    const videoNode = video.track?.attach();
    if (videoNode) {
      element.replaceChildren(videoNode);
    }

    this.isPlayingVideo = true;
  }

  @action
  stopVideo(): void {
    const video = this.liveKitSubscriber.getTrackPublication(
      this.liveKitSubscriber.isScreenShareEnabled ? Track.Source.ScreenShare : Track.Source.Camera
    );
    video?.setEnabled(false);
    video?.track?.detach();
    this.isPlayingVideo = false;
  }

  @action
  private handlePublisherChanged(event: TrackPublication | RemoteTrack, isActive: boolean): void {
    if (event.kind === Track.Kind.Audio) {
      this.hasAudio = isActive;
      if (isActive) {
        this.startAudioTrack();
      } else {
        this.stopAudioTrack();
      }
    } else if (event.kind === Track.Kind.Video) {
      this.hasVideo = isActive;
    }
    this.onUserMediaChangedHandler &&
      this.onUserMediaChangedHandler(
        this.uid,
        event.kind === Track.Kind.Audio ? ProviderMediaType.Audio : ProviderMediaType.Video,
        isActive
      );
  }

  @action
  private handleVolumeLevelChanged(speakingValue: boolean): void {
    this.volumeLevel = speakingValue ? 100 : 0;
    if (this.onUserVolumeLevelChanged) {
      this.onUserVolumeLevelChanged(this.uid, this.volumeLevel);
    }
  }

  private stopAudioTrack(): void {
    if (this.restartAudioTimeoutId !== null) {
      clearTimeout(this.restartAudioTimeoutId);
      this.restartAudioTimeoutId = null;
    }
    if (this.resumeAudioTimeoutId !== null) {
      clearTimeout(this.resumeAudioTimeoutId);
      this.resumeAudioTimeoutId = null;
    }

    const trackPublication = this.liveKitSubscriber.getTrackPublication(Track.Source.Microphone);

    if (trackPublication?.track) {
      if (this.audioPlaybackFailedHandler) {
        trackPublication.track.off('audioPlaybackFailed', this.audioPlaybackFailedHandler);
        this.audioPlaybackFailedHandler = null;
      }

      if (this.audioPlaybackStartedHandler) {
        trackPublication.track.off('audioPlaybackStarted', this.audioPlaybackStartedHandler);
        this.audioPlaybackStartedHandler = null;
      }

      trackPublication.track.detach();
    }

    if (this.audio) {
      this.audio.pause();
      if (this.audio.src) {
        this.audio.src = '';
        this.audio.load();
      }
    }

    this.audio = null;
  }

  async changeSpeaker(): Promise<void> {
    if (!this.parent.devices.audiooutput || !this.audio || !this.parent.room) {
      return;
    }

    const result = await this.parent.room.switchActiveDevice(
      'audiooutput',
      this.parent.devices.audiooutput
    );
    // if can not find active device, switch to default;
    if (!result) {
      await this.parent.room.switchActiveDevice('audiooutput', 'default');
    }
  }

  private restartAudioTrack(): void {
    this.stopAudioTrack();
    this.isStartingAudio = false;
    this.restartAudioTimeoutId = setTimeout(() => {
      this.startAudioTrack();
    }, 100);
  }

  private tryResumeAudioPlayback(onRetryFailed?: (error: unknown) => void): void {
    if (!this.audio || !this.audio.paused || this.audio.ended) {
      return;
    }

    if (this.resumeAudioTimeoutId !== null) {
      clearTimeout(this.resumeAudioTimeoutId);
    }

    this.resumeAudioTimeoutId = setTimeout(() => {
      if (!this.audio || !this.audio.paused || this.audio.ended) {
        return;
      }

      this.audio
        .play()
        .then(() => {
          this.changeSpeaker();
        })
        .catch((error) => {
          if (onRetryFailed) {
            onRetryFailed(error);
          } else {
            this.restartAudioTrack();
          }
        });
    }, 100);
  }

  async startAudioTrack(): Promise<void> {
    if (this.isStartingAudio) {
      return;
    }

    const track = this.liveKitSubscriber.getTrackPublication(Track.Source.Microphone)?.track;

    if (!track) {
      return;
    }

    this.isStartingAudio = true;

    try {
      this.stopAudioTrack();

      this.audioPlaybackStartedHandler = (): void => {
        this.changeSpeaker();
      };

      this.audioPlaybackFailedHandler = async (): Promise<void> => {
        if (this.audio) {
          try {
            const playPromise = this.audio.play();
            if (playPromise !== undefined) {
              await playPromise;
            }
          } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
              this.tryResumeAudioPlayback();
            } else {
              this.restartAudioTrack();
            }
          }
        } else {
          this.restartAudioTrack();
        }
      };

      track.on('audioPlaybackStarted', this.audioPlaybackStartedHandler);
      track.on('audioPlaybackFailed', this.audioPlaybackFailedHandler);

      const audio = track.attach();
      this.audio = audio;

      try {
        await this.audio.play();
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          this.tryResumeAudioPlayback((retryError) => {
            if (!(retryError instanceof DOMException && retryError.name === 'AbortError')) {
              console.warn('Audio play retry failed:', retryError);
              this.restartAudioTrack();
            }
          });
        } else {
          console.error('Audio play error:', error);
        }
      }

      this.changeSpeaker();
    } finally {
      this.isStartingAudio = false;
    }
  }
}
